// src/components/PostForm.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeo } from '../context/GeoContext';
import { useAnonConsent } from '../context/AnonConsentContext';
import '../styles/common.css';
import LimitedTextarea from './LimitedTextarea';
import { apiPost, bootstrapBffSession } from '../lib/apiClient';
import { getRecaptchaToken } from '../lib/recaptcha';

export default function PostForm({ onPost, surveyId = null }) {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { coords } = useGeo();
  const { consented } = useAnonConsent();

  // Ensure stable server identity cookie exists at mount
  useEffect(() => {
    bootstrapBffSession().catch(() => {});
  }, []);

  const handlePost = async () => {
    if (!content.trim()) return;
    if (!consented) {
      setError('Read-only mode: enable anonymous ID participation on the ID Policy page to post.');
      return;
    }
    setError(null);

    // Geolocation optional: include if available
    const lat = Number.isFinite(coords?.lat) ? coords.lat : null;
    const lng = Number.isFinite(coords?.lng) ? coords.lng : null;

    try {
      await bootstrapBffSession(); // ensure same user_id across actions
      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('post_create');
      } catch (err) {
        setError(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }

      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const created = await apiPost('/posts', { content, lat, lng, survey_id: surveyId }, options);

      setContent('');
      if (typeof onPost === 'function') onPost(created);
    } catch (e) {
      // Inline moderation error (HTTP 422)
      const isMod = e && e.status === 422;
      const msg = e?.msg || e?.message || (isMod ? 'Content not allowed.' : 'Failed to create post');
      setError(msg);
      console.error('Error creating post:', e);
    }
  };

  return (
    <div>
      <LimitedTextarea
        className="comment-textarea"
        wrapperClassName="comment-box"
        counterClassName="comment-char-counter"
        placeholder="Share your opinion about this survey..."
        value={content}
        onChange={(e) => { setContent(e.target.value); if (error) setError(null); }}
        rows={1}
        minRows={1}
        maxRows={5}
        autoGrow
        max={240}
      />
      <div className="post-controls">
        <button onClick={handlePost} className="post-button post-button--compact" disabled={!!error || !consented}>
          {consented ? 'Post' : 'Enable participation to post'}
        </button>
      </div>

      {error && (
        <div className="error" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
      {!consented && (
        <div className="muted" style={{ marginTop: 8 }}>
          Read-only mode: visit the <a href="/privacy/anonymous-id">ID Policy</a> page to enable participation.
        </div>
      )}
    </div>
  );
}
